import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { MenuItemType } from '@next-nx/utils/types'
import { useFindMenu, useTrans, useTealium } from '@next-nx/hooks'
import {
  ChevronDown,
  ChevronUp,
  Close,
  Logo,
  Menu,
  Search,
  AreaClienti,
} from '@next-nx/shared-ui/icons'
import { getAnchorRel, getTimestamp } from '@next-nx/utils'
import { Button, NumeroVerde, WContainer } from '@next-nx/shared-ui/atoms'
import { NavDropdown, NavMobile, SearchOverlay } from '@next-nx/shared-ui/organisms'
import styles from './Header.module.sass'

interface TopMenuItemProps {
  item: MenuItemType
}

interface Props {
  className?: string
  setopenPreventivoModal?: (v: boolean) => void
}

const TopMenuItem = ({ item }: TopMenuItemProps) => {
  const [open, setOpen] = useState(false)
  const itemRef = useRef(null)
  const { linkDataLayer } = useTealium()

  const handleClick = (action_type = '', cta = '-') => {
    const action_timestamp = getTimestamp()
    action_type =
      action_type.toLowerCase() === 'scopri il noleggio a lungo termine'
        ? 'scopri_il_nlt'
        : action_type

    linkDataLayer({
      virtual: 'action',
      action_name: 'navigazione',
      action_tech: 'click',
      action_type: action_type.replace(/ /g, '_').toLocaleLowerCase(),
      action_timestamp,
      action_effect: 'navigazione_header',
      cta: cta.replace(/ /g, '_').toLowerCase(),
    })
  }

  return (
    <div
      className={`${styles.topMenuItem} ${open ? styles['topMenuItem--open'] : ''}`}
      ref={itemRef}
      onBlur={(e) => {
        if (itemRef.current && !itemRef.current.contains(e.relatedTarget)) {
          setOpen(false)
        }
      }}
    >
      {item.url ? (
        <div className={styles.topMenuItemMain}>
          <Link href={item.url}>
            <a
              rel={getAnchorRel(item.url)}
              className={styles.topMenuItemLabel}
              onClick={() => handleClick(item.name)}
            >
              {item.name}
            </a>
          </Link>
          {!!item.children?.edges.filter((x) => !!x.node.url).length && (
            <button
              role="button"
              className={`${styles.topMenuItemArrow} ${
                styles.topMenuItemTrigger
              } ${item.name?.toHotjarFormat('dropdown_{0}')}`}
              onClick={() => setOpen(!open)}
            >
              {open ? <ChevronUp /> : <ChevronDown />}
            </button>
          )}
        </div>
      ) : (
        <button
          role="button"
          className={`${styles.topMenuItemMain} ${styles.topMenuItemTrigger}`}
          onClick={() => setOpen(!open)}
        >
          <span className={styles.topMenuItemLabel}>{item.name}</span>
          <span className={styles.topMenuItemArrow}>{open ? <ChevronUp /> : <ChevronDown />}</span>
        </button>
      )}
      {!!item.children?.edges.filter((x) => !!x.node.url).length && (
        <div className={styles.topMenuItemDropdown}>
          {item.children.edges
            .filter((x) => !!x.node.url)
            .map((x) => (
              <Link href={x.node.url || ''} key={x.node.pk}>
                <a
                  className={styles.topMenuItemSubitem}
                  rel={getAnchorRel(x.node.url || '')}
                  onClick={() => handleClick(item.name, x.node.name)}
                >
                  {x.node.name}
                </a>
              </Link>
            ))}
        </div>
      )}
    </div>
  )
}

const Header = (props: Props) => {
  const { className = '', setopenPreventivoModal = () => {} } = props
  const t = useTrans()
  const [searchActive, setSearchActive] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<number | null | undefined>(null)
  const [navMobileOpen, setNavMobileOpen] = useState(false)

  const topMenu = useFindMenu('top')
  const mainMenu = useFindMenu('main')
  const mobileMainMenu = useFindMenu('main-mobile')
  const { linkDataLayer } = useTealium()

  const handleClick = (action_type = '', interazione?: string) => {
    const dateTime = new Date().toLocaleString('it-IT', { timeZone: 'Europe/Rome' })
    const fullDate = dateTime.slice(0, 9).split('/').reverse().join('-')
    const fullTime = dateTime.slice(-8)
    const action_timestamp = `${fullDate} ${fullTime}`

    linkDataLayer({
      virtual: 'action',
      action_name: 'navigazione',
      action_tech: 'click',
      action_type: action_type.replace(/\.|\/|\s+/g, '_').toLocaleLowerCase(),
      action_timestamp,
      action_effect: 'navigazione_header',
    })

    if (action_type === 'area_riservata') {
      linkDataLayer({
        virtual: 'action',
        action_name: action_type,
        action_tech: 'click',
        action_type: 'accedi',
        action_timestamp,
        action_effect: 'accesso_area_riservata',
      })
    }

    if (interazione && interazione === 'contattaci_cta') {
      linkDataLayer({
        virtual: 'action',
        action_name: 'contattaci',
        action_tech: 'click',
        action_type: 'accedi',
        action_timestamp,
        action_effect: 'accesso_pagina_contatti',
      })
    }
  }

  useEffect(() => {
    if (navMobileOpen) document.body.classList.add('w-noscrollnav')
    else document.body.classList.remove('w-noscrollnav')
  }, [navMobileOpen])

  useEffect(() => {
    if (searchActive) document.body.classList.add('w-noscroll')
    else document.body.classList.remove('w-noscroll')
  }, [searchActive])

  return (
    <>
      <header className={`${styles.header} ${className}`}>
        {topMenu && topMenu.children && topMenu.children.edges.length > 0 && (
          <div className={styles.top}>
            <WContainer variant="lg">
              <div className={styles.topMenu}>
                <div className={`${styles.topMenuItem} ${styles['topMenuItem--numero-verde']}`}>
                  <a href="#numero-verde">
                    <NumeroVerde
                      variant="horizontal"
                      noBackground
                      backgroundOnNumber
                      phoneLabel={t('Numero Verde gratuito')}
                      description={t('Hai bisogno del <strong>soccorso stradale</strong>?')}
                    />
                  </a>
                </div>
                {topMenu.children.edges
                  .filter((x) => !!x.node.url || !!x.node.children?.edges.length)
                  .map((x) => (
                    <TopMenuItem item={x.node} key={x.node.pk} />
                  ))}
              </div>
            </WContainer>
          </div>
        )}
        <div className={styles.main} onMouseLeave={() => setActiveDropdown(null)}>
          <WContainer variant="lg">
            <div className={styles.mainContent}>
              <Link href="/">
                <a className={styles.logo} onClick={() => handleClick('logo')}>
                  <Logo />
                </a>
              </Link>
              {mainMenu && mainMenu?.children && mainMenu.children.edges.length > 0 && (
                <div className={styles.mainMenu}>
                  {mainMenu.children.edges.map((x) =>
                    x.node.url ? (
                      <Link href={x.node.url} key={x.node.pk}>
                        <a
                          key={x.node.pk}
                          className={`${styles.mainMenuItem} ${
                            activeDropdown === x.node.pk ? styles['mainMenuItem--active'] : ''
                          } ${x.node.name?.toHotjarFormat('dropdown_{0}')}`}
                          onMouseEnter={() =>
                            setActiveDropdown(activeDropdown === x.node.pk ? null : x.node.pk)
                          }
                          onClick={() => handleClick(x.node.name)}
                        >
                          {x.node.name}
                        </a>
                      </Link>
                    ) : (
                      <button
                        key={x.node.pk}
                        className={`${styles.mainMenuItem} ${
                          activeDropdown === x.node.pk ? styles['mainMenuItem--active'] : ''
                        }`}
                        onMouseEnter={() =>
                          setActiveDropdown(activeDropdown === x.node.pk ? null : x.node.pk)
                        }
                        onClick={() => handleClick(x.node.name)}
                      >
                        {x.node.name}
                      </button>
                    )
                  )}
                </div>
              )}
              <div className={styles.actions}>
                <Button
                  label={t('Cerca')}
                  icon={<Search />}
                  color="lightblue"
                  variant="ghost"
                  onClick={() => {
                    setSearchActive(true)
                    handleClick('cerca')
                  }}
                  className={'funzionalità_ricerca'}
                />
                <Button
                  label={t('Area clienti')}
                  icon={<AreaClienti />}
                  color="lightblue"
                  variant="ghost"
                  href="https://unipolrental.link/areaclienti"
                  newPage={true}
                  onClick={() => handleClick('area_riservata')}
                />
                <Button
                  label={t('Contattaci')}
                  href="/contattaci"
                  onClick={() => handleClick('contattaci', 'contattaci_cta')}
                />
              </div>
              <div className={styles.actionsMobile}>
                <Button
                  icon={<Search />}
                  size="lg"
                  color="blue"
                  variant="ghost"
                  onClick={() => {
                    setSearchActive(true)
                    handleClick('cerca')
                  }}
                  className={'funzionalità_ricerca_mobile'}
                />
                <Button
                  icon={<AreaClienti />}
                  size="lg"
                  color="blue"
                  variant="ghost"
                  href="https://unipolrental.link/areaclienti"
                  newPage={true}
                  onClick={() => handleClick('area_riservata')}
                />
                <Button
                  icon={navMobileOpen ? <Close /> : <Menu />}
                  size="lg"
                  color="blue"
                  variant="ghost"
                  onClick={() => setNavMobileOpen(!navMobileOpen)}
                  className={'menu_mobile'}
                />
              </div>
            </div>
          </WContainer>
          {mainMenu &&
            mainMenu?.children &&
            mainMenu.children.edges.length > 0 &&
            mainMenu.children.edges.map((x) => (
              <NavDropdown
                item={x.node}
                className={styles.navDropdown}
                open={activeDropdown === x.node.pk}
                key={x.node.pk}
                setopenPreventivoModal={setopenPreventivoModal}
              />
            ))}
        </div>
      </header>
      {!!mainMenu && !!mobileMainMenu && (
        <NavMobile
          item={mainMenu}
          commonItem={mobileMainMenu}
          open={navMobileOpen}
          setOpen={setNavMobileOpen}
        />
      )}
      <SearchOverlay visible={searchActive} setVisible={setSearchActive} />
    </>
  )
}

export default Header
